import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Spinner,
  Avatar,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Switch,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import TrackingPageLayout from "components/TrackingPageLayout";
import { useMemo, useState, useCallback } from "react";
import {
  useMyStudents,
  useStudentActiveTrips,
  useStudentTripHistory,
  useStudentPayments,
  useStudentPresenceHistory,
  useStudentStatistics,
} from "hooks/useTrackingStudents";
import { showToast, toastTypes } from "utils";

function formatDate(s: string | null | undefined) {
  if (!s) return "—";
  return new Date(s).toLocaleString("fr-FR");
}

function formatAmount(value: number | string | undefined) {
  if (value == null) return "—";
  const n = typeof value === "string" ? parseFloat(value) : value;
  return Number.isNaN(n) ? "—" : `${n.toFixed(2)} FCFA`;
}

export default function TrackingStudentDetailPage() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const id = studentId ? parseInt(studentId, 10) : NaN;
  const validId = Number.isInteger(id) ? id : null;

  const { students, updateAvailability, refetch: refetchStudents } = useMyStudents();
  const student = useMemo(
    () => (validId ? students.find((s) => s.id === validId) : null),
    [students, validId]
  );

  const { trips: activeTrips, isLoading: loadingActive } = useStudentActiveTrips(validId);
  const { results: tripHistory, isLoading: loadingHistory } = useStudentTripHistory(validId);
  const { results: payments, isLoading: loadingPayments } = useStudentPayments(validId);
  const { results: presenceHistory, isLoading: loadingPresence } =
    useStudentPresenceHistory(validId);
  const { statistics, isLoading: loadingStats } = useStudentStatistics(validId);

  const [togglingAvailability, setTogglingAvailability] = useState(false);

  const handleAvailabilityChange = useCallback(
    async (checked: boolean) => {
      if (!validId) return;
      setTogglingAvailability(true);
      try {
        await updateAvailability({
          student_id: validId,
          is_available: checked,
        });
        await refetchStudents();
        showToast({
          toast,
          message: checked ? "Élève marqué disponible." : "Élève marqué indisponible.",
          status: toastTypes.success,
        });
      } catch (e: any) {
        const msg =
          e?.response?.data?.error || e?.message || "Erreur lors de la mise à jour.";
        showToast({ toast, message: msg, status: toastTypes.error });
      } finally {
        setTogglingAvailability(false);
      }
    },
    [validId, updateAvailability, refetchStudents, toast]
  );

  if (!validId) {
    return (
      <TrackingPageLayout title="Détail élève">
        <Text color="red.600" px={4} pt={4}>Identifiant élève invalide.</Text>
      </TrackingPageLayout>
    );
  }

  if (!student && students.length > 0) {
    return (
      <TrackingPageLayout title="Détail élève">
        <Flex justify="center" py={8}><Spinner size="lg" /></Flex>
      </TrackingPageLayout>
    );
  }

  if (!student) {
    return (
      <TrackingPageLayout title="Détail élève">
        <Text color="gray.600" px={4} pt={4}>Élève introuvable.</Text>
      </TrackingPageLayout>
    );
  }

  const displayName =
    student.first_name || student.last_name
      ? `${student.first_name ?? ""} ${student.last_name ?? ""}`.trim()
      : student.username ?? "Sans nom";

  return (
    <TrackingPageLayout title={displayName}>
      <Flex align="center" gap={4} mb={6} flexWrap="wrap" px={4} pt={4}>
        <Avatar
          size="xl"
          name={displayName}
          src={
            typeof student.profile_picture === "string" ? student.profile_picture : undefined
          }
        />
        <Box flex={1}>
          <Heading size="md">{displayName}</Heading>
          {student.institution_name && (
            <Text color="gray.600">{student.institution_name}</Text>
          )}
          {student.address_label && (
            <Text fontSize="sm" color="gray.500">{student.address_label}</Text>
          )}
          <HStack mt={2} spacing={2}>
            {student.is_available !== undefined && (
              <Badge colorScheme={student.is_available ? "green" : "orange"}>
                {student.is_available ? "Disponible" : "Indisponible"}
              </Badge>
            )}
            {student.has_active_trip && <Badge colorScheme="blue">Trajet en cours</Badge>}
          </HStack>
        </Box>
        <FormControl display="flex" alignItems="center" w="auto">
          <FormLabel mb={0} mr={2}>
            Disponible
          </FormLabel>
          <Switch
            isChecked={student.is_available !== false}
            onChange={(e) => handleAvailabilityChange(e.target.checked)}
            isDisabled={togglingAvailability}
          />
        </FormControl>
      </Flex>

      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>Trajets actifs</Tab>
          <Tab>Historique trajets</Tab>
          <Tab>Paiements</Tab>
          <Tab>Présence</Tab>
          <Tab>Statistiques</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            {loadingActive ? (
              <Spinner size="sm" />
            ) : activeTrips.length === 0 ? (
              <Text color="gray.600">Aucun trajet en cours.</Text>
            ) : (
              <VStack align="stretch" spacing={2}>
                {activeTrips.map((trip: any) => (
                  <Box key={trip.id} p={3} borderWidth="1px" borderRadius="md">
                    <Text fontWeight="medium">
                      {trip.bus?.name ?? "—"} – {trip.route?.name ?? "—"}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Début : {formatDate(trip.started_at)} · Chauffeur :{" "}
                      {trip.driver_username ?? "—"}
                    </Text>
                  </Box>
                ))}
              </VStack>
            )}
          </TabPanel>
          <TabPanel>
            {loadingHistory ? (
              <Spinner size="sm" />
            ) : tripHistory.length === 0 ? (
              <Text color="gray.600">Aucun trajet dans l&apos;historique.</Text>
            ) : (
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Bus / Route</Th>
                    <Th>Début</Th>
                    <Th>Fin</Th>
                    <Th>Statut</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {tripHistory.map((trip: any) => (
                    <Tr key={trip.id}>
                      <Td>
                        {trip.bus?.name ?? "—"} – {trip.route?.name ?? "—"}
                      </Td>
                      <Td>{formatDate(trip.started_at)}</Td>
                      <Td>{formatDate(trip.finished_at)}</Td>
                      <Td>{trip.status ?? "—"}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </TabPanel>
          <TabPanel>
            {loadingPayments ? (
              <Spinner size="sm" />
            ) : payments.length === 0 ? (
              <Text color="gray.600">Aucun paiement.</Text>
            ) : (
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Montant</Th>
                    <Th>Statut</Th>
                    <Th>Date</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {payments.map((p: any) => (
                    <Tr key={p.id}>
                      <Td>{formatAmount(p.amount)}</Td>
                      <Td>{p.status ?? "—"}</Td>
                      <Td>{formatDate(p.paid_at ?? p.created_at)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </TabPanel>
          <TabPanel>
            {loadingPresence ? (
              <Spinner size="sm" />
            ) : presenceHistory.length === 0 ? (
              <Text color="gray.600">Aucun enregistrement de présence.</Text>
            ) : (
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>Présent</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {presenceHistory.map((p: any, idx: number) => (
                    <Tr key={p.id ?? idx}>
                      <Td>{formatDate(p.date ?? p.created_at)}</Td>
                      <Td>{p.present ? "Oui" : "Non"}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </TabPanel>
          <TabPanel>
            {loadingStats ? (
              <Spinner size="sm" />
            ) : (
              <VStack align="stretch" spacing={2}>
                {statistics.total_trips != null && (
                  <Text>
                    <strong>Total trajets :</strong> {statistics.total_trips}
                  </Text>
                )}
                {statistics.total_present != null && (
                  <Text>
                    <strong>Présences :</strong> {statistics.total_present}
                  </Text>
                )}
                {statistics.total_absent != null && (
                  <Text>
                    <strong>Absences :</strong> {statistics.total_absent}
                  </Text>
                )}
                {Object.keys(statistics).length === 0 && (
                  <Text color="gray.600">Aucune statistique disponible.</Text>
                )}
              </VStack>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </TrackingPageLayout>
  );
}
